/* ============================================================
   Common UI Components
   ============================================================ */
   import React from 'react';
   import './Common.css';
   
   /* ── Button ─────────────────────────────────────── */
   export const Button = ({
     children,
     variant = 'primary',
     size = 'md',
     loading = false,
     disabled = false,
     icon,
     iconRight,
     fullWidth = false,
     className = '',
     type = 'button',
     ...props
   }) => (
     <button
       type={type}
       disabled={disabled || loading}
       className={`btn btn-${variant} btn-${size}${fullWidth ? ' btn-full' : ''}${loading ? ' btn-loading' : ''} ${className}`}
       {...props}
     >
       {loading && <span className="btn-spinner" />}
       {!loading && icon && <span className="btn-icon">{icon}</span>}
       {children && <span>{children}</span>}
       {!loading && iconRight && <span className="btn-icon">{iconRight}</span>}
     </button>
   );
   
   /* ── FormField ───────────────────────────────────── */
   export const FormField = ({
     label,
     name,
     error,
     required,
     hint,
     children,
     className = '',
   }) => (
     <div className={`form-field${error ? ' has-error' : ''} ${className}`}>
       {label && (
         <label htmlFor={name} className="form-label">
           {label}
           {required && <span className="form-required">*</span>}
         </label>
       )}
       {children}
       {hint && !error && <p className="form-hint">{hint}</p>}
       {error && <p className="form-error">{error}</p>}
     </div>
   );
   
   /* ── Input ───────────────────────────────────────── */
   export const Input = React.forwardRef(({ className = '', error, ...props }, ref) => (
     <input
       ref={ref}
       className={`form-input${error ? ' input-error' : ''} ${className}`}
       {...props}
     />
   ));
   Input.displayName = 'Input';
   
   /* ── PasswordInput — avec bouton afficher/masquer ── */
   export const PasswordInput = React.forwardRef(({ className = '', error, placeholder, ...props }, ref) => {
     const [visible, setVisible] = React.useState(false);
     return (
       <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
         <input
           ref={ref}
           type={visible ? 'text' : 'password'}
           placeholder={placeholder || '••••••••'}
           className={`form-input${error ? ' input-error' : ''} ${className}`}
           style={{ paddingRight: 40, width: '100%' }}
           {...props}
         />
         <button
           type="button"
           onClick={() => setVisible(v => !v)}
           tabIndex={-1}
           style={{
             position: 'absolute', right: 10,
             background: 'none', border: 'none', cursor: 'pointer',
             color: 'var(--text-muted)', padding: 4,
             display: 'flex', alignItems: 'center',
             transition: 'color .15s',
           }}
           title={visible ? 'Masquer' : 'Afficher'}
           onMouseEnter={e => e.currentTarget.style.color = 'var(--brand-accent)'}
           onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
         >
           {visible ? (
             /* Œil barré */
             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
               <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
               <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
               <line x1="1" y1="1" x2="23" y2="23"/>
             </svg>
           ) : (
             /* Œil ouvert */
             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
               <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
               <circle cx="12" cy="12" r="3"/>
             </svg>
           )}
         </button>
       </div>
     );
   });
   PasswordInput.displayName = 'PasswordInput';
   
   /* ── Textarea ────────────────────────────────────── */
   export const Textarea = React.forwardRef(({ className = '', error, rows = 4, ...props }, ref) => (
     <textarea
       ref={ref}
       rows={rows}
       className={`form-input form-textarea${error ? ' input-error' : ''} ${className}`}
       {...props}
     />
   ));
   Textarea.displayName = 'Textarea';
   
   /* ── Select ──────────────────────────────────────── */
   export const Select = React.forwardRef(({ className = '', error, children, ...props }, ref) => (
     <select
       ref={ref}
       className={`form-input form-select${error ? ' input-error' : ''} ${className}`}
       {...props}
     >
       {children}
     </select>
   ));
   Select.displayName = 'Select';
   
   /* ── Modal ───────────────────────────────────────── */
   export const Modal = ({ isOpen, onClose, title, children, size = 'md', footer }) => {
     const mouseDownTarget = React.useRef(null);

     React.useEffect(() => {
       if (isOpen) {
         document.body.style.overflow = 'hidden';
       } else {
         document.body.style.overflow = '';
       }
       return () => { document.body.style.overflow = ''; };
     }, [isOpen]);

     if (!isOpen) return null;

     return (
       <div className="modal-overlay"
         onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
         onClick={(e) => { if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) onClose(); }}
       >
         <div className={`modal modal-${size} fade-in`} role="dialog" aria-modal="true">
           <div className="modal-header">
             <h3 className="modal-title">{title}</h3>
             <button className="modal-close" onClick={onClose} aria-label="Fermer">
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                 <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
               </svg>
             </button>
           </div>
           <div className="modal-body">{children}</div>
           {footer && <div className="modal-footer">{footer}</div>}
         </div>
       </div>
     );
   };
   
   /* ── Card ────────────────────────────────────────── */
   export const Card = ({ children, className = '', padding = 'md', ...props }) => (
     <div className={`card card-pad-${padding} ${className}`} {...props}>
       {children}
     </div>
   );
   
   /* ── Badge ───────────────────────────────────────── */
   export const StatusBadge = ({ status }) => {
     const map = {
       // Devis
       pending:   ['En attente', 'badge-warning'],
       sent:      ['Envoyé', 'badge-info'],
       accepted:  ['Accepté', 'badge-success'],
       rejected:  ['Refusé', 'badge-error'],
       expired:   ['Expiré', 'badge-neutral'],
       converted: ['Converti', 'badge-accent'],
       // Factures
       draft:     ['Brouillon', 'badge-neutral'],
       paid:      ['Payé', 'badge-success'],
       partial:   ['Partiel', 'badge-warning'],
       overdue:   ['En retard', 'badge-error'],
       cancelled: ['Annulé', 'badge-neutral'],
       // Commandes
       new:        ['Nouvelle', 'badge-accent'],
       processing: ['En cours', 'badge-info'],
       quoted:     ['Devisé', 'badge-success'],
       // Blog
       published:  ['Publié', 'badge-success'],
       archived:   ['Archivé', 'badge-neutral'],
     };
     const [label, cls] = map[status] ?? [status, 'badge-neutral'];
     return <span className={`badge ${cls}`}>{label}</span>;
   };
   
   /* ── Confirm Dialog ──────────────────────────────── */
   export const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, confirmLabel = 'Confirmer', danger = false }) => (
     <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm"
       footer={
         <div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}>
           <Button variant="ghost" onClick={onClose}>Annuler</Button>
           <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Button>
         </div>
       }
     >
       <p style={{color:'var(--text-secondary)',lineHeight:1.6}}>{message}</p>
     </Modal>
   );
   
   /* ── Empty State ─────────────────────────────────── */
   export const EmptyState = ({ icon, title, description, action }) => (
     <div className="empty-state">
       {icon && <div className="empty-icon">{icon}</div>}
       <h3 className="empty-title">{title}</h3>
       {description && <p className="empty-desc">{description}</p>}
       {action}
     </div>
   );
   
   /* ── Pagination ──────────────────────────────────── */
   export const Pagination = ({ meta, onPageChange }) => {
     if (!meta || meta.total_pages <= 1) return null;
     const pages = [];
     const { page, total_pages } = meta;
     for (let i = Math.max(1, page - 2); i <= Math.min(total_pages, page + 2); i++) pages.push(i);

     return (
       <div className="pagination">
         <button className="page-btn" onClick={() => onPageChange(1)} disabled={!meta.has_prev} title="Première page">«</button>
         <button className="page-btn" onClick={() => onPageChange(page - 1)} disabled={!meta.has_prev} title="Page précédente">‹</button>
         {pages[0] > 1 && <><button className="page-btn" onClick={() => onPageChange(1)}>1</button>{pages[0] > 2 && <span className="page-ellipsis">…</span>}</>}
         {pages.map(p => (
           <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPageChange(p)}>{p}</button>
         ))}
         {pages[pages.length-1] < total_pages && <><span className="page-ellipsis">…</span><button className="page-btn" onClick={() => onPageChange(total_pages)}>{total_pages}</button></>}
         <button className="page-btn" onClick={() => onPageChange(page + 1)} disabled={!meta.has_next} title="Page suivante">›</button>
         <button className="page-btn" onClick={() => onPageChange(total_pages)} disabled={!meta.has_next} title="Dernière page">»</button>
         <span className="page-info">{meta.total} résultats</span>
       </div>
     );
   };
   